import type {
	AbsoluteCoordinate,
	DropOverAlignmentValue,
	DropOverProps
} from "client/Components";
import { Coordinate } from "client/Misc/commonTypes";
import { EMPTY_OBJECT } from "client/Misc/utils";
import {
	useCallback,
	useMemo,
	useState
} from "react";

export type OpenItemMenuState = {
	isOpen: true;
	isContextMenu: false;
} | {
	isOpen: true;
	isContextMenu: true;
	coordinate: Coordinate;
};

export type ItemMenuState =
	| OpenItemMenuState
	| { isOpen: false; };

export type ItemMenuHandle = {
	menuState: ItemMenuState;
	onOpenContextMenu: (coordinate: Coordinate) => void;
	onOpenMoreMenu: () => void;
	onCloseMenu: () => void;
};

export function useItemMenuState(): ItemMenuHandle {
	const [contextMenuState, setContextMenuState] = useState<ContextMenuState>({ isOpen: false });
	const [isMoreMenuOpen, setIsMoreMenuOpen] = useState<boolean>(false);

	const openContextMenuHandler = useCallback(
		(coordinate: Coordinate): void => {
			setContextMenuState({
				isOpen: true,
				coordinate
			});
		},
		[]
	);

	const closeContextMenuHandler = useCallback(
		(): void => {
			setContextMenuState({isOpen: false});
		},
		[]
	);

	const openMoreMenuHandler = useCallback(
		(): void => {
			setIsMoreMenuOpen(true);
		},
		[]
	);

	const closeMoreMenuHandler = useCallback(
		(): void => {
			setIsMoreMenuOpen(false);
		},
		[]
	);

	const closeHandler = useMemo(
		(): (() => void) => {
			return contextMenuState.isOpen
				? closeContextMenuHandler
				: closeMoreMenuHandler;
		},
		[
			closeContextMenuHandler,
			closeMoreMenuHandler,
			contextMenuState.isOpen
		]
	);

	const state = useMemo(
		(): ItemMenuState => {
			if (contextMenuState.isOpen) {
				return {
					isOpen: true,
					isContextMenu: true,
					coordinate: contextMenuState.coordinate
				};
			}

			if (isMoreMenuOpen) {
				return {
					isOpen: true,
					isContextMenu: false
				};
			}

			return {
				isOpen: false
			};
		},
		[
			contextMenuState,
			isMoreMenuOpen
		]
	);

	return {
		menuState: state,
		onOpenContextMenu: openContextMenuHandler,
		onOpenMoreMenu: openMoreMenuHandler,
		onCloseMenu: closeHandler
	};
}

export type ItemDropOverMenuProps = Pick<
	DropOverProps,
	| "contentAlignment"
	| "targetAlignment"
	| "closeOnMouseDown"
>;

export type ItemDropOverMenuOptions = {
	moreMenuContentAlignment?: DropOverAlignmentValue;
	contextMenuContentAlignment?: DropOverAlignmentValue;
	moreMenuTargetAlignment?: DropOverAlignmentValue;
};

export function useItemDropOverMenuProps(
	state: OpenItemMenuState,
	options: ItemDropOverMenuOptions = EMPTY_OBJECT
): ItemDropOverMenuProps {
	const targetAlignment = useMemo(
		(): AbsoluteCoordinate | DropOverAlignmentValue | undefined => {
			if (state.isContextMenu) {
				return {
					kind: "absolute",
					coordinate: state.coordinate
				};
			}

			return options.moreMenuTargetAlignment;
		},
		[
			options.moreMenuTargetAlignment,
			state
		]
	);

	const contentAlignment = useMemo(
		(): DropOverAlignmentValue | undefined => {
			return state.isContextMenu
				? options.contextMenuContentAlignment
				: options.moreMenuContentAlignment;
		},
		[
			options.contextMenuContentAlignment,
			options.moreMenuContentAlignment,
			state.isContextMenu
		]
	);

	return {
		targetAlignment,
		contentAlignment,
		closeOnMouseDown: state.isContextMenu
	};
};

type ContextMenuState = {
	isOpen: false;
} | {
	isOpen: true;
	coordinate: Coordinate;
};